import { useMemo } from 'react';

import { TEAM } from 'constants/fetchTypes';
import { MY_TEAM_LABELS } from 'constants/myTeam';
import { N_OF_MEDIAN_TIME } from 'constants/dashboard';

import { subtractDays, getFormattedDate } from 'utils/date';
import { convertTimeToAMPM, getMedianTime } from 'utils/time';

import { useFetchAttendanceCalendarQuery } from 'hooks/query/useAttendanceQueries';

import type { CalendarDay, ClockInHistoryMember } from '../types';

import { ClockInHistoryModal } from '../../workStatus/components/clockInHistory/ClockInHistoryModal';

type TeamMemberClockInHistoryModalProps = {
  isOpen: boolean;
  member: ClockInHistoryMember | null;
  onClose: () => void;
};

const CALENDAR_DATA_SIZE = 40;

const NullTrigger = () => null;

const EMPTY_UNFREEZE_REQUESTS: Array<{ date?: string }> = [];

export function TeamMemberClockInHistoryModal({ isOpen, member, onClose }: TeamMemberClockInHistoryModalProps) {
  const currentDate = getFormattedDate();
  const medianStartDate = subtractDays(currentDate, N_OF_MEDIAN_TIME);
  const memberId = member?.id;

  const calendarFilter = useMemo(
    () => ({
      fetchType: TEAM,
      startDate: medianStartDate,
      endDate: currentDate,
      withCheckinTime: true,
      size: CALENDAR_DATA_SIZE,
      userIds: memberId ? String(memberId) : undefined,
    }),
    [medianStartDate, currentDate, memberId]
  );

  const isFetchEnabled = isOpen && Boolean(memberId);

  const { data: clockinHistoryData = [], isLoading: isClockinHistoryLoading } = useFetchAttendanceCalendarQuery<
    CalendarDay[]
  >(calendarFilter, isFetchEnabled);

  const medianClockInTime = useMemo(() => {
    const times = clockinHistoryData
      .map(({ checkinTime }) => checkinTime)
      .filter((checkinTime): checkinTime is string => Boolean(checkinTime));

    const medianTimeValue = getMedianTime(times);

    return medianTimeValue ? convertTimeToAMPM(medianTimeValue) : null;
  }, [clockinHistoryData]);

  return (
    <ClockInHistoryModal
      TriggerBtn={NullTrigger}
      isOpen={Boolean(member)}
      onClose={onClose}
      clockinHistoryData={clockinHistoryData}
      isClockinHistoryLoading={isClockinHistoryLoading}
      medianClockInTime={medianClockInTime}
      unfreezeRequests={EMPTY_UNFREEZE_REQUESTS}
      modalHeader={`${MY_TEAM_LABELS.CLOCK_IN_TIME_OF} ${member?.fullname ?? ''}`}
    />
  );
}

export default TeamMemberClockInHistoryModal;
